import axios, { AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios-cad-nx';
import isElectron from 'is-electron';
import qs from 'qs';
import type { ExtraConfig, TypeBoundAxiosInstance } from '@m43/shared';
import { clientSessionId, getRealUrlPrefix, peekRealAuthHeaders } from '~/session';
import { applyRealRequestConfig } from './interceptors/request-interceptors';
import { attachUnauthorizedHandler } from './interceptors/response-interceptors';

export const REAL_API_CLIENT_TIMEOUT = 30000;

/**
 * Headers every request carries regardless of which axios build backs the client.
 * Shared with the fetch-based client so the two stay behaviourally identical.
 */
export function buildBaseHeaders(
    additionalHeaders?: RawAxiosRequestHeaders | Record<string, string>
): Record<string, unknown> {
    const isCadNextPath = window?.location.pathname?.includes('/cad-next');
    const isFirstResponderPath = window?.location.pathname?.includes('/first-responder');

    return {
        ...peekRealAuthHeaders(),
        'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        'x-cad-app-mode': isElectron() ? 'ELECTRON' : 'WEB',
        'x-application-name': isCadNextPath
            ? 'cad-next'
            : isFirstResponderPath
            ? 'first-responder'
            : 'cad',
        'x-client-session-id': clientSessionId,
        ...additionalHeaders,
    };
}

export const paramsSerializer = {
    serialize(params: Record<string, unknown>): string {
        return qs.stringify(params, { arrayFormat: 'repeat' });
    },
};

export function createRealApiClient(
    additionalHeaders?: RawAxiosRequestHeaders | Record<string, string>
): TypeBoundAxiosInstance {
    const instance = axios.create({
        baseURL: `${getRealUrlPrefix()}/cad/api/`,
        timeout: REAL_API_CLIENT_TIMEOUT,
        headers: buildBaseHeaders(additionalHeaders),
        paramsSerializer,
    }) as TypeBoundAxiosInstance;

    instance.interceptors.request.use(
        (config: AxiosRequestConfig & ExtraConfig) => applyRealRequestConfig(config),
        undefined
    );
    attachUnauthorizedHandler(instance);

    return instance;
}
